import { faCakeCandles, faEnvelope, faPlus, faTrash, faTriangleExclamation, faUserGear } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { ColumnConfig, DataTable } from 'src/components/Table/DataTable';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { ClientSearchResult } from 'src/models/user/_types';
import { fetchUserAlertsWithBirthdate } from 'src/models/user/user';
import { getBirthdayMessage } from 'src/models/user/utils';
import { ClientAutocomplete } from 'src/shared/ClientAutocomplete';
import { useMessageDialog } from 'src/shared/Dialogs/MessageDialog';
import { Identifier } from 'src/types/utilityTypes';
import { formatFullDateAndTime } from 'src/utils/dateFormatter';

import type { Appointment, WaitListEntry } from 'src/models/appointment/_types';

interface WaitlistSectionProps {
  appointment: Appointment;
  canManage: boolean;
  onAddToWaitlist?: (client: ClientSearchResult) => void;
  onAddToSession?: (entry: WaitListEntry) => void;
  onRemoveEntry?: (entry: WaitListEntry) => void;
}

// Row type for DataTable - extends WaitListEntry with required id
type WaitlistRow = WaitListEntry & { id: Identifier };

/**
 * WaitlistSection - Waitlist entries with management using DataTable
 *
 * Matches v2 modal-new-appointment.hbs lines 1127-1206:
 * - "Waitlist (count)" header
 * - "Add Client to Waitlist" autocomplete
 * - Table with Account, Added, Actions columns
 * - Entry rows with name, alert icons, email/phone, date, action icons
 */
export const WaitlistSection: FC<WaitlistSectionProps> = ({
  appointment,
  canManage,
  onAddToWaitlist,
  onAddToSession,
  onRemoveEntry,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.viewEventDialog.waitlistSection' });
  const entries = appointment.entries || [];

  // Convert entries to rows format for DataTable
  const rows: WaitlistRow[] = entries.map((entry) => ({
    ...entry,
    id: entry.id,
  }));

  const handleClientSelect = (
    client: ClientSearchResult | ClientSearchResult[] | null
  ) => {
    if (client && !Array.isArray(client)) {
      onAddToWaitlist?.(client);
    }
  };

  // Column definitions for DataTable
  const columns: ColumnConfig[] = [
    {
      id: 'account',
      label: t('account'),
      format: (_value, row: WaitlistRow) => (
        <AccountCell entry={row} />
      ),
    },
    {
      id: 'createdAt',
      label: t('added'),
      format: (value) => formatFullDateAndTime(value)
    },
  ];

  // Row actions as ActionConfig array - matches v2 lines 1169-1196
  const rowActions = [
    {
      icon: faUserGear,
      label: t('manageClient'),
      href: (row: WaitlistRow) => `/ex4/clients/${row.clientId}`,
      target: '_blank',
      showAction: (row: WaitlistRow) => !!row.clientId,
    },
    {
      icon: faPlus,
      label: t('addToSession'),
      onClick: (row: WaitlistRow) => onAddToSession?.(row),
      showAction: () => canManage,
    },
    {
      icon: faTrash,
      label: t('removeFromWaitlist'),
      onClick: (row: WaitlistRow) => onRemoveEntry?.(row),
      showAction: () => canManage,
    },
  ];

  return (
    <Stack spacing={2}>
      {/* Add to Waitlist autocomplete - v2 lines 1132-1142 */}
      {canManage && (appointment.scheduledAppointmentId || appointment.id) && (
        <ClientAutocomplete
          label={t('addClientToWaitlist')}
          placeholder={t('searchForClient')}
          onChange={handleClientSelect}
          value={null}
        />
      )}

      {/* Table using DataTable - v2 lines 1145-1204 */}
      <DataTable
        columns={columns}
        rows={rows}
        rowActions={rowActions}
        fixedActions
        loading={false}
        noResultsText={t('waitlistEmpty')}
      />
    </Stack>
  );
};

interface AccountCellProps {
  entry: WaitListEntry;
}

/**
 * AccountCell - Renders the account column content with name, alert icons, and contact info
 * Separated as a component because UserAlertIcons uses hooks
 */
const AccountCell: FC<AccountCellProps> = ({ entry }) => {
  const userName = `${entry.firstName || ''} ${entry.lastName || ''}`.trim();

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" fontWeight="medium">
          {entry.firstName} {entry.lastName}
        </Typography>
        <UserAlertIcons userId={entry.userId} userName={userName} />
      </Box>
      {entry.email && (
        <Typography variant="caption" color="text.secondary" display="block">
          {entry.email}
        </Typography>
      )}
      {entry.phoneNumber && (
        <Typography variant="caption" color="text.secondary" display="block">
          {entry.phoneNumber}
        </Typography>
      )}
    </>
  );
};

interface UserAlertIconsProps {
  userId: Identifier;
  userName?: string;
}

/**
 * UserAlertIcons - Message, alert, and birthday icons for a user
 *
 * Matches v2 user-alert-icons component (user-alert-icons.hbs):
 * 1. Message icon (fa-envelope) - always shown, "Send Message"
 * 2. Alert icon (fa-alert/triangle-exclamation) - if user has alerts
 * 3. Birthday icon (fa-birthday-cake/cake-candles) - if birthday within 7 days
 */
const UserAlertIcons: FC<UserAlertIconsProps> = ({ userId, userName }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.viewEventDialog.waitlistSection' });
  // Message dialog hook for sending messages to this user
  const { openMessageDialog, MessageDialogComponent } = useMessageDialog();

  const { data: alertsData } = useQuery({
    queryKey: ['userAlerts', userId],
    queryFn: () => fetchUserAlertsWithBirthdate(userId),
    enabled: !!userId,
  });

  const hasAlerts = alertsData?.alerts && alertsData.alerts.length > 0;
  const hasRedAlert = alertsData?.alerts?.some((a) => a.color === 'red');
  const alertMessage = alertsData?.alerts?.map((a) => a.message).join(', ');
  const birthdayMessage = getBirthdayMessage(alertsData?.birthdate ?? null);

  return (
    <>
      {/* Message icon - v2 user-alert-icons line 1-8 */}
      {userId && (
        <Tooltip title={t('sendMessage')}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openMessageDialog({
                recipientId: String(userId),
                recipientNickname: userName || '',
              });
            }}
            sx={{ p: 0.25 }}
          >
            <Icon
              icon={faEnvelope}
              size="xsmall"
            />
          </IconButton>
        </Tooltip>
      )}

      {/* Alert icon - v2 user-alert-icons line 11-18 */}
      {hasAlerts && (
        <Tooltip title={alertMessage || t('alert')}>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <Icon
              icon={faTriangleExclamation}
              size="xsmall"
              sx={{ color: hasRedAlert ? 'error.main' : 'warning.main' }}
            />
          </Box>
        </Tooltip>
      )}

      {/* Birthday icon - v2 user-alert-icons line 21-28 */}
      {birthdayMessage && (
        <Tooltip title={birthdayMessage}>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <Icon
              icon={faCakeCandles}
              size="xsmall"
            />
          </Box>
        </Tooltip>
      )}

      {/* Message Dialog */}
      <MessageDialogComponent />
    </>
  );
};
